import { useEffect, useLayoutEffect, useRef } from 'react';
import type { RefObject } from 'react';
import type { MapRef } from 'react-map-gl/maplibre';
import type maplibregl from 'maplibre-gl';
import type { FeatureCollection } from 'geojson';

import type { Client } from '@/clients/types';
import type { FeatureOverlayState } from '@/components/map/utils/mapLayers';
import { addOrbitLayer, ORBIT_LAYER_IDS } from '@/components/map/utils/mapLayerOrder';
import { fetchGridTilesForClient } from '@/api/gridTilesApi';
import { EMPTY_FEATURE_COLLECTION, setGeoJsonSourceData } from '@/utils/mapUtils';
import { showErrorToast } from '@/errors/showErrorToast';

const GRID_TILES_SOURCE_ID = 'orbit-grid-tiles-source';

// Grid tiles are overview context that must stay visible across the whole Network Overview
// band — including further out than a single viewport would allow. A viewport bbox at those
// wider scales exceeds the backend's 2-square-degree limit, so we load the client's tiles in
// one shot (GET /gridtiles) instead of per-viewport, and rely on the semantic-zoom system to
// confine rendering to the Network Overview level (they turn off as you zoom in).
function registerGridTilesSourceAndLayers(mlMap: maplibregl.Map, data: FeatureCollection, visible: boolean): void {
    const visibility = visible ? 'visible' : 'none';

    try {
        if (mlMap.getSource(GRID_TILES_SOURCE_ID)) {
            (mlMap.getSource(GRID_TILES_SOURCE_ID) as maplibregl.GeoJSONSource).setData(data);
        } else {
            mlMap.addSource(GRID_TILES_SOURCE_ID, { type: 'geojson', data });
        }
    } catch {
        return;
    }

    if (!mlMap.getLayer(ORBIT_LAYER_IDS.gridTilesFill)) {
        try {
            addOrbitLayer(
                mlMap,
                {
                    id: ORBIT_LAYER_IDS.gridTilesFill,
                    type: 'fill',
                    source: GRID_TILES_SOURCE_ID,
                    layout: { visibility },
                    paint: {
                        'fill-color': '#f59e0b',
                        'fill-opacity': 0.1,
                    },
                },
                'areaOverlays',
            );
        } catch {
            // Progressive enhancement — layer failures must not break the map.
        }
    }

    if (!mlMap.getLayer(ORBIT_LAYER_IDS.gridTilesOutline)) {
        try {
            addOrbitLayer(
                mlMap,
                {
                    id: ORBIT_LAYER_IDS.gridTilesOutline,
                    type: 'line',
                    source: GRID_TILES_SOURCE_ID,
                    layout: { visibility },
                    paint: {
                        'line-color': '#d97706',
                        'line-width': 1.5,
                        'line-opacity': 0.8,
                    },
                },
                'areaOverlays',
            );
        } catch {
            // Progressive enhancement — layer failures must not break the map.
        }
    }

    if (!mlMap.getLayer(ORBIT_LAYER_IDS.gridTilesLabels)) {
        try {
            addOrbitLayer(
                mlMap,
                {
                    id: ORBIT_LAYER_IDS.gridTilesLabels,
                    type: 'symbol',
                    source: GRID_TILES_SOURCE_ID,
                    layout: {
                        visibility,
                        'text-field': ['get', 'ref'],
                        // Use a font the OpenFreeMap glyphs endpoint hosts; the MapLibre default
                        // ("Open Sans Regular,Arial Unicode MS Regular") 404s against it.
                        'text-font': ['Noto Sans Regular'],
                        'text-size': 14,
                        'text-anchor': 'center',
                        'text-max-width': 6,
                        'text-allow-overlap': false,
                        'text-ignore-placement': false,
                    },
                    paint: {
                        'text-color': '#92400e',
                        'text-halo-color': '#ffffff',
                        // Wider halo keeps the larger labels legible over the basemap and tile fill.
                        'text-halo-width': 2,
                    },
                },
                'businessLabels',
            );
        } catch {
            // Progressive enhancement — layer failures must not break the map.
        }
    }
}

/**
 * Loads all Grid Tiles for the selected client in one request (GET /gridtiles), registers the
 * MapLibre GeoJSON source and three layers (fill, outline, labels), and re-registers them after
 * every base map style change. Loading the whole set (rather than per-viewport) keeps tiles
 * available across the entire Network Overview band, including zoomed further out than a single
 * viewport bbox could request. Visibility is set at registration time from
 * `featureOverlays.gridTiles`; subsequent toggle + semantic-zoom changes are handled by
 * `useMapLayerVisibility` via the populated `gridTiles.mapLayerIds` (which confine tiles to the
 * Network Overview level).
 *
 * Safe when:
 *   - No client is selected (clears source data, skips fetch).
 *   - Grid Tile data has not loaded yet (source contains empty FeatureCollection).
 *   - Map layers do not exist (all operations guard with getLayer/getSource).
 *   - The API request fails; the error is surfaced and the map continues working.
 */
export function useGridTilesLayer(
    mapRef: RefObject<MapRef | null>,
    isMapLoaded: boolean,
    selectedClient: Client | null,
    featureOverlays: FeatureOverlayState,
): void {
    const gridTilesDataRef = useRef<FeatureCollection>(EMPTY_FEATURE_COLLECTION);

    // Keep a ref so the registration effect can read the current overlay state
    // without re-running when featureOverlays changes (useMapLayerVisibility
    // owns subsequent visibility toggling via mapLayerIds).
    const featureOverlaysRef = useRef(featureOverlays);
    useLayoutEffect(() => {
        featureOverlaysRef.current = featureOverlays;
    });

    // Load all grid tiles for the client once (not per-viewport) when
    // client/map-ready/enabled state changes, so they stay available at every zoom.
    useEffect(() => {
        gridTilesDataRef.current = EMPTY_FEATURE_COLLECTION;
        const mlMap = mapRef.current?.getMap();
        if (mlMap) setGeoJsonSourceData(mlMap, GRID_TILES_SOURCE_ID, EMPTY_FEATURE_COLLECTION);

        if (!selectedClient || !isMapLoaded) return;
        if (!featureOverlays.gridTiles) return;

        let cancelled = false;
        fetchGridTilesForClient(selectedClient.id)
            .then((data) => {
                if (cancelled) return;
                gridTilesDataRef.current = data;
                const m = mapRef.current?.getMap();
                if (m) setGeoJsonSourceData(m, GRID_TILES_SOURCE_ID, data);
            })
            .catch((err: unknown) => {
                if (!cancelled) showErrorToast(err);
            });
        return () => {
            cancelled = true;
        };
    }, [selectedClient, isMapLoaded, featureOverlays.gridTiles, mapRef]);

    // Register Grid Tile source and layers when the map is ready, and re-register on
    // every style.load (base map switches reload all sources and layers).
    useEffect(() => {
        if (!isMapLoaded) return;
        const mlMap = mapRef.current?.getMap();
        if (!mlMap) return;

        const register = () => {
            registerGridTilesSourceAndLayers(mlMap, gridTilesDataRef.current, featureOverlaysRef.current.gridTiles);
        };

        register();
        mlMap.on('style.load', register);

        return () => {
            mlMap.off('style.load', register);
        };
    }, [isMapLoaded, mapRef]);
}
